import type { LinkedModuleDiffItem } from "./TagDiffModal";

/** Per-module row from `UserTagVersion.modulesSnapshot`. */
export type VersionModuleSnapshotRow = {
  id: number;
  name: string | null;
  enabled: boolean;
  priority: number;
  gated: boolean | null;
  tagContent: string | null;
};

/**
 * Build Tab 2 linked-module JS diffs between two version snapshots.
 * Only includes modules marked `gated: true` with differing `tagContent`.
 * Legacy rows (`gated` / `tagContent` null) are skipped.
 */
export function buildLinkedModuleDiffsFromSnapshots(
  leftSnapshot: readonly VersionModuleSnapshotRow[] | null | undefined,
  rightSnapshot: readonly VersionModuleSnapshotRow[] | null | undefined
): LinkedModuleDiffItem[] {
  const leftRows = leftSnapshot ?? [];
  const rightRows = rightSnapshot ?? [];

  const leftById = new Map(leftRows.map((r) => [r.id, r]));
  const rightById = new Map(rightRows.map((r) => [r.id, r]));

  const ids = new Set<number>();
  leftRows.forEach((r) => {
    if (r.gated === true) ids.add(r.id);
  });
  rightRows.forEach((r) => {
    if (r.gated === true) ids.add(r.id);
  });

  const items: (LinkedModuleDiffItem & { priority: number })[] = [];

  ids.forEach((id) => {
    const left = leftById.get(id);
    const right = rightById.get(id);
    const leftCode = left?.tagContent ?? "";
    const rightCode = right?.tagContent ?? "";
    if (leftCode === rightCode) return;

    const name = right?.name || left?.name || `モジュール #${id}`;
    const priority = right?.priority ?? left?.priority ?? 0;

    items.push({
      id: String(id),
      name,
      left: leftCode,
      right: rightCode,
      priority,
    });
  });

  return items
    .sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name))
    .map(({ id, name, left, right }) => ({ id, name, left, right }));
}
